import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { VisaService } from '@modules/payment/services/visa.service';
import { PaymentService } from '@app/data-services/payment.service';
import { AuthService } from '@app/services/auth/auth.service';
import { AppSettings } from '@app/helpers/app.settings';
import { environment } from '@env/environment';
import { RoNotificationService } from '@app/modules/shared/services/ro-notification.service';
import { NgRoDebugger } from '@modules/shared/classes/NgRoDebugger';
import { combineLatest, EMPTY } from 'rxjs';
import { catchError, switchMap, take, takeUntil } from 'rxjs/operators';
import { OnDestroyService } from '@modules/shared/services/on-destroy.service';
import { MemberService } from '@app/data-services/member.service';
import { PEN } from '@app/helpers/currency-functions';
import { HttpErrorResponse } from '@angular/common/http';
import { NzMessageService, NzModalService } from 'ng-zorro-antd';

declare var window: any;

const VISA_MERCHANT_ID = environment.visa.merchantId;
const VISA_FORM_SRC = environment.visa.paymentButtonLib;
const INTERSOCIO_TIMEOUT_URL = AppSettings.API_ENDPOINT_VISA.timeoutUrl;
const VISA_FORM_ACTION = AppSettings.API_ENDPOINT_VISA.redirectionUrl;
const INTERSOCIO_CONFIRN_URL = AppSettings.API_ENDPOINT_VISA.confirmUrl;
const INTERSOCIO_LOGO_URL = `${environment.baseUrlApp}assets/img/public/logo_for_visa.png`;

@Component({
    selector: 'app-payment-visa',
    providers: [VisaService, AuthService],
    templateUrl: './payment-visa.component.html',
})
export class PaymentVISAComponent extends NgRoDebugger implements OnInit {
    @Output() onError: EventEmitter<void> = new EventEmitter();

    totalToPay: string;
    appUser: AppUser;

    constructor(
        private _visaService: VisaService,
        private _roNotificationService: RoNotificationService,
        private paymentService: PaymentService,
        private onDestroy$: OnDestroyService,
        private message: NzMessageService,
        private memberService: MemberService,
        private modalService: NzModalService
    ) {
        super('PaymentVISAComponent');
    }

    loadingVisa = false;
    niubizQrPaymentWarningActive = environment.niubizQrPaymentWarningActive;
    niubizQrPaymentWarningVisible = environment.niubizQrPaymentWarningActive;

    static _generateUrlActionVisa(sessionToken: any, transactionId: any) {
        const redirectionUrl = encodeURI(INTERSOCIO_CONFIRN_URL);
        return `${VISA_FORM_ACTION}?redirect_to=${redirectionUrl}&session_token=${sessionToken}&transaction_id=${transactionId}`;
    }

    ngOnInit() {
        delete window.VisanetCheckout;

        combineLatest([this.paymentService.paymentList$, this.paymentService.commission$])
            .pipe(takeUntil(this.onDestroy$.onDestroy$), take(1))
            .subscribe(([payDebts, commission]) => {
                this.totalToPay = PEN(commission.totalWithCommission).value.toString();
                this.getTransactionSessionToken(this.totalToPay, payDebts);
            });
    }

    private getTransactionSessionToken(amount: string, debt_ids: Debt[]) {
        this.loadingVisa = true;
        this.logger('Getting transaction ID');

        this.memberService.appUser$
            .pipe(
                switchMap((appUser) => {
                    this.appUser = appUser;
                    return this._visaService.getSessionToken({
                        amount,
                        debt_ids,
                    });
                }),
                catchError((error: HttpErrorResponse) => {
                    if (error.status === 400) {
                        this.modalService.error({
                            nzTitle: 'Hubo un error con los datos',
                            nzContent: 'Al parecer the lista de items a pagar se ha modificado. Vuelva al inicio',
                            nzOnOk: () => {
                                this.onError.emit();
                            },
                        });
                        return EMPTY;
                    }

                    this.message.error('No se pudo obtener una respuesta de parte de VISA', { nzDuration: 10000 });
                    this.loadingVisa = false;
                    return EMPTY;
                })
            )
            .subscribe((visaSession: any) => {
                if (visaSession.sessionKey) {
                    this.logger('Success session key');
                    this.logger('Creating payment form');
                    this._createVisaForm(
                        visaSession.sessionKey,
                        visaSession.transaction_id,
                        visaSession.visa_user_token_id
                    );
                } else {
                    this.logger('Error getting the session key');
                    this._roNotificationService.danger('Hubo un error estableciendo la conexión con VISA.');
                }
                this.loadingVisa = false;
            });
    }

    private _createVisaForm(sessionToken, transactionId, userToken) {
        const script = document.createElement('script');
        script.setAttribute('src', VISA_FORM_SRC);
        script.setAttribute('data-sessiontoken', sessionToken);
        script.setAttribute('data-channel', 'web');
        script.setAttribute('data-merchantid', VISA_MERCHANT_ID);
        script.setAttribute('data-buttonsize', 'LARGE');
        script.setAttribute('data-buttoncolor', 'NAVY');
        script.setAttribute('data-merchantlogo', INTERSOCIO_LOGO_URL);
        script.setAttribute('data-merchantname', 'Club Internacional Arequipa');
        script.setAttribute('data-formbuttoncolor', '#0A0A2A');
        script.setAttribute('data-showamount', 'TRUE');
        script.setAttribute('data-purchasenumber', transactionId);
        script.setAttribute('data-amount', this.totalToPay);
        script.setAttribute('data-expirationminutes', '5');
        script.setAttribute('data-timeouturl', INTERSOCIO_TIMEOUT_URL);
        script.setAttribute('data-usertoken', this.appUser.uuid);

        const form = document.getElementById('visa-form');
        form.setAttribute('action', PaymentVISAComponent._generateUrlActionVisa(sessionToken, transactionId));
        form.appendChild(script);
    }
}
